import '../statistics.css'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { DateSelectRange, PageHeader, Spinner } from '../../../Components'
import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { useCapsule } from '../../../State/Functions'
import { translations } from '../../../translations'
import { PieTotalPricingType } from './PieTotalPricingType'
import { PricingTimeline } from './PricingTimeline'

const t = translations[cap.language.state || 'IT'] || translations.IT

/**
 * Configurazione delle categorie dei grafici a torta per il dashboard pricing.
 * Ogni voce contiene chiave dati, titolo visualizzato e chiave per i totali.
 */
const PIE_CATEGORIES = [
  { key: 'general', dataKey: 'generalPricing', title: t.stPieGenerale },
  { key: 'trasferta', dataKey: 'trasfertaPricing', title: t.stChartTrasferteCreate },
  { key: 'openPauseConversation', dataKey: 'openPauseConversation', title: t.stPieOpenPausa },
  {
    key: 'ignoredConversation',
    dataKey: 'ignoredConversation',
    title: t.stChartConversazioniIgnorate
  },
  { key: 'errorHandledPricing', dataKey: 'errorHandledPricing', title: t.stPieErroriGestiti },
  { key: 'noraIsExcluded', dataKey: 'noraIsExcludedPricing', title: t.stPieNoraEsclusaOp },
  { key: 'error', dataKey: 'errorPricing', title: t.errore }
]

/**
 * Dashboard per la visualizzazione dei costi per provider e timeline.
 * Mostra grafici a torta per categoria e un grafico scatter temporale.
 * @returns {JSX.Element} Pagina dashboard pricing
 */
export const DashboardChartInfoPricing = () => {
  const navigate = useNavigate()
  const [startDate, setStartDate] = useCapsule(cap.selectedStartDateDashboard)
  const [endDate, setEndDate] = useCapsule(cap.selectedEndDateDashboard)
  const [totalCostPerProvider, setTotalCostPerProvider] = useState([])
  const [providers, setProviders] = useState([])
  const { data: conversationDataPricing, isSuccess: isSuccessPricing } = useCQuery([
    'totalPricing',
    startDate,
    endDate
  ])

  /**
   * Processa tutti i dati pricing e aggiorna lo stato per i grafici
   * @param {Object} data - Dati pricing dal server
   */
  const settingsAllDataDashboard = (data) => {
    const {
      generalPricing,
      trasfertaPricing,
      errorPricing,
      noraIsExcludedPricing,
      errorHandledPricing,
      openPauseConversation,
      ignoredConversation
    } = data || {}

    const generalProvidersListKey = generalPricing[0]?.providerTotals || {}
    setProviders(
      Object.keys(generalProvidersListKey)?.map((key) => ({
        provider: key,
        disabled: false,
        company: generalProvidersListKey[key]?.company
      }))
    )

    setTotalCostPerProvider({
      general: setTotalCostPerProviderType(generalPricing),
      trasferta: setTotalCostPerProviderType(trasfertaPricing),
      error: setTotalCostPerProviderType(errorPricing),
      noraIsExcluded: setTotalCostPerProviderType(noraIsExcludedPricing),
      errorHandledPricing: setTotalCostPerProviderType(errorHandledPricing),
      openPauseConversation: setTotalCostPerProviderType(openPauseConversation),
      ignoredConversation: setTotalCostPerProviderType(ignoredConversation)
    })
  }

  /**
   * Trasforma i dati provider in formato per il grafico a torta
   * @param {Array} data - Array con i totali per provider
   * @returns {Array} Dati formattati per PieChart
   */
  const setTotalCostPerProviderType = (data) => {
    const providersListKey = data[0]?.providerTotals || {}
    if (providersListKey) {
      return (
        Object.keys(providersListKey)
          ?.map((key) => ({ name: key, value: providersListKey[key]?.total }))
          .filter((item) => item.value) || []
      )
    }
  }

  useEffect(() => {
    if (isSuccessPricing && !providers?.length) {
      settingsAllDataDashboard(conversationDataPricing)
    }
  }, [conversationDataPricing])

  if (!isSuccessPricing) return <Spinner />

  return (
    <div className="stats-pricing-container">
      <PageHeader
        title={t.dashboardPricing}
        actions={
          <button type="button" className="nbtn" onClick={() => navigate('/statistics/')}>
            <i className="pi pi-chevron-left" style={{ fontSize: 14 }} />
            {t.stTornaStatistiche}
          </button>
        }
      />

      <div className="stats-pricing-toolbar">
        <DateSelectRange startDate={[startDate, setStartDate]} endDate={[endDate, setEndDate]} />
      </div>

      {/* Pie Charts Grid */}
      <div className="stats-pricing-grid">
        {PIE_CATEGORIES.map(({ key, dataKey, title }) => {
          const amount = conversationDataPricing?.[dataKey]?.[0]?.total?.toFixed(2) || '0.00'
          return (
            <div className="stats-pricing-card" key={key}>
              <div className="stats-pricing-card-header">
                <span className="stats-pricing-card-title">{title}</span>
                <span className="stats-pricing-card-amount">{amount}€</span>
              </div>
              <div className="stats-pricing-card-chart">
                <PieTotalPricingType data={totalCostPerProvider[key] || []} />
              </div>
            </div>
          )
        })}
      </div>

      {/* Timeline */}
      <PricingTimeline dates={[startDate, endDate]} />
    </div>
  )
}
